import identity from 'lodash/identity';
import React, { useState } from 'react';
import TagManager from 'react-gtm-module';
import type { TagManagerArgs } from 'react-gtm-module';

import type { Country } from '@skippay/core/@types/country';

import GoogleAnalyticsContext, { Context } from './context';
import { enhanceEvent, enhancePageview, pushNonEmptyToDataLayer } from './global';
import { GA4Event, GA4Pageview, GAEvent, GAPageview } from './types';

function isThenable<T extends Record<string, unknown>>(obj: T | Promise<T>): obj is Promise<T> {
	return typeof obj.then === `function`;
}

const AnalyticsProvider: React.FC<Props> = ({
	children,
	options,
	mapObject = identity,
	country,
}) => {
	const [context] = useState<Context>(() => {
		TagManager.initialize(options);
		const mapAndPush = (obj: GAEvent | GA4Event | GAPageview | GA4Pageview) => {
			const mapped = mapObject(obj);
			if (isThenable(mapped)) {
				mapped.then(pushNonEmptyToDataLayer);
			} else {
				pushNonEmptyToDataLayer(mapped);
			}
		};
		const sendEvent: Context['sendEvent'] = (eventData, eventExtra) =>
			mapAndPush(enhanceEvent(eventData, { ...eventExtra, country }));
		const sendPageview: Context['sendPageview'] = (pageview, previousLocation) =>
			mapAndPush(enhancePageview({ ...pageview, country }, previousLocation));
		return { sendEvent, sendPageview };
	});
	return (
		<GoogleAnalyticsContext.Provider value={context}>{children}</GoogleAnalyticsContext.Provider>
	);
};

export type Props = {
	children: React.ReactNode;
	options: TagManagerArgs;
	mapObject?<T extends GAEvent | GA4Event | GAPageview | GA4Pageview>(object: T): T | Promise<T>;
	country: Country;
};

export default AnalyticsProvider;
